import { createSelector } from 'reselect';
import { RootState } from '../../../types';
import { EntityTypes } from '../../../constants/entities';
import { IEntityReducer } from 'core/reducers/entities';
import { IVariableDAO } from 'types/domain/variable';
import { parseVariableDAO } from './variable';
import { getEntities } from '../utils/getEntities';
import { ICourseUserVariable, ICourseUserVariableDAO, ICourseVariable, ICourseVariableDAO } from 'types/domain/course';
import { selectCourseModules } from './courseModules';
import { IModuleDAO } from 'types/domain/module';
import { IAction } from 'types/domain/action';
import { selectCourseNodesAndEdges } from './flow';
import { reduceArrayById } from 'utils/reduceArrayById';

const entity = (state: RootState) => state.entity.present;
const courseId = (state: RootState, id?: number) => id || state.state[EntityTypes.COURSE];
const courseNodeId = (state: RootState, id?: number) => id || state.state[EntityTypes.COURSE_NODE];
const search = (state: RootState) => state.core.search;
const modules = selectCourseModules;
const courseNodes = selectCourseNodesAndEdges;

export const selectVariablesInCourse = createSelector(
  [entity, modules, search, courseId],
  (entity: IEntityReducer, modules, search, id: number) => {
    return modules.reduce((prev: IVariableDAO[], next: IModuleDAO) => {
      const variable = parseVariableDAO(entity, next.variableId);
      if (variable) prev.push(variable);

      next.actions.forEach((action: IAction) => {
        if (action.variableId && !prev.some(v => v.id === action.variableId)) {
          const variable = parseVariableDAO(entity, action.variableId);
          if (variable) prev.push(variable);
        }
      });

      return prev;
    }, []);
  },
);

export const selectCourseNodeVariables = createSelector(
  [entity, courseId, courseNodes],
  (entity: IEntityReducer, id, courseNodes) => {
    const { nodes, edges } = courseNodes;

    const courseVariablesById = reduceArrayById(
      getEntities(entity, EntityTypes.COURSE_VARIABLE, 'courseId', id),
      'variableId',
    );

    return nodes
      .map(n => entity[EntityTypes.COURSE_NODE][n.data.id as number])
      .filter(cn => !!cn.variableId && cn.variableId !== entity[EntityTypes.COURSE][id]?.variableId)
      .map(cn => {
        const varId = cn.variableId || entity[EntityTypes.MODULE][cn.moduleId]?.variableId;
        return {
          courseVariable: courseVariablesById[varId],
          variable: parseVariableDAO(entity, varId),
        };
      });
  },
);

export const selectCourseVariables = createSelector([entity, courseId], (entity: IEntityReducer, id) =>
  getEntities(entity, EntityTypes.COURSE_VARIABLE, 'courseId', id)
    .sort((a, b) => (a.order > b.order ? 1 : -1))
    .map((cv: ICourseVariable) => {
      return {
        ...cv,
        variable: parseVariableDAO(entity, cv.variableId),
        modules: getEntities(entity, EntityTypes.MODULE, 'variableId', cv.variableId),
      } as ICourseVariableDAO;
    }),
);

export const selectCourseUserVariables = createSelector(
  [entity, courseNodeId],
  (entity: IEntityReducer, courseNodeId) => {
    return getEntities(entity, EntityTypes.COURSE_USER_VARIABLE, 'courseNodeId', courseNodeId).map(
      (cv: ICourseUserVariable) => {
        const courseVariable = entity[EntityTypes.COURSE_VARIABLE][cv.courseVariableId];
        return {
          ...cv,
          courseVariable: {
            ...courseVariable,
            variable: parseVariableDAO(entity, courseVariable.variableId),
            modules: getEntities(entity, EntityTypes.MODULE, 'variableId', courseVariable.variableId),
          },
          organisationUserVariable: entity[EntityTypes.ORG_USER_VARIABLE][cv.organisationUserVariableId],
        } as ICourseUserVariableDAO;
      },
    );
  },
);
